import { useQuery } from '@tanstack/react-query';
import type { StoryMedia } from '@kick/types';
import { api } from './api';

export interface Anuncio {
  id: string;
  mediaUrl: string;
  mediaType: StoryMedia;
  posterUrl: string | null;
  caption: string | null;
  durationSeconds: number;
  publishedAt: string;
  expiresAt: string;
  /** Los tres nulos en un anuncio general: no lleva a ningún comercio. */
  merchantId: string | null;
  merchantName: string | null;
  merchantSlug: string | null;
  merchantLogo: string | null;
}

/**
 * Los anuncios vivos.
 *
 * Sin sesión: la portada la abre cualquiera y la publicidad tiene que verla
 * justo quien todavía no tiene cuenta.
 *
 * Se refrescan cada minuto y al volver a la pestaña. Duran 24 horas, así que
 * no es por verlos aparecer al segundo: es porque un anuncio se retira o se
 * borra desde el panel en cualquier momento, y una pestaña abierta seguía
 * mostrando durante minutos uno que ya no existe. Eso no se lee como caché:
 * se lee como que el panel y la tienda no se ponen de acuerdo.
 *
 * La consulta es pública, sin sesión y devuelve un puñado de filas: el minuto
 * no cuesta nada.
 */
export function useAnuncios() {
  return useQuery({
    queryKey: ['anuncios'],
    queryFn: async () => (await api.get<Anuncio[]>('/anuncios/vigentes')).data,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

const CLAVE_VISTOS = 'kick.anuncios.vistos';

/**
 * Cuáles ya vio esta persona, en su propio navegador.
 *
 * No se guarda en el servidor a propósito: la portada la ve gente sin cuenta,
 * y para saber si el aro va encendido o apagado no hace falta registrar quién
 * miró qué. Se limpia solo, porque solo se conservan los que siguen vivos.
 */
export function leerVistos(): Set<string> {
  try {
    const crudo = localStorage.getItem(CLAVE_VISTOS);
    return new Set(crudo ? (JSON.parse(crudo) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function marcarVisto(id: string, vigentes: string[]): Set<string> {
  const vistos = leerVistos();
  vistos.add(id);

  // Solo se recuerdan los que todavía existen: si no, la lista crece sin fin.
  const podados = [...vistos].filter((v) => vigentes.includes(v));

  try {
    localStorage.setItem(CLAVE_VISTOS, JSON.stringify(podados));
  } catch {
    // Modo incógnito o almacenamiento lleno: el aro se verá encendido siempre,
    // que es un detalle estético y no impide ver el anuncio.
  }

  return new Set(podados);
}
